/**
 * @engine/input — Control Scheme
 *
 * Defines a set of device requirements for a control scheme
 * (e.g., "Keyboard+Mouse", "Gamepad", "Touch").
 */

import type { ControlSchemeDef, DeviceType, DeviceId } from '../types';
import type { IInputDevice } from '../devices/InputDevice';

/**
 * Control scheme — represents a device configuration.
 * Used to filter bindings and auto-switch based on device activity.
 */
export class ControlScheme {
  readonly name: string;
  readonly devices: readonly DeviceType[];
  readonly bindingGroups: readonly string[];

  constructor(def: ControlSchemeDef) {
    this.name = def.name;
    this.devices = def.devices;
    this.bindingGroups = def.bindingGroups ?? [];
  }

  /**
   * Check if this scheme matches the connected devices.
   * A scheme matches if ALL required device types are present.
   */
  matches(connectedDevices: ReadonlyMap<DeviceId, IInputDevice>): boolean {
    const connectedTypes = new Set<DeviceType>();
    for (const device of connectedDevices.values()) {
      if (device.connected) {
        connectedTypes.add(device.type);
      }
    }

    for (const required of this.devices) {
      if (!connectedTypes.has(required)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if this scheme supports a given device type
   */
  supportsDevice(type: DeviceType): boolean {
    return this.devices.includes(type);
  }

  /**
   * Serialize to definition
   */
  toDefinition(): ControlSchemeDef {
    return {
      name: this.name,
      devices: [...this.devices],
      bindingGroups: this.bindingGroups.length > 0 ? [...this.bindingGroups] : undefined,
    };
  }

  /**
   * Create from definition
   */
  static fromDefinition(def: ControlSchemeDef): ControlScheme {
    return new ControlScheme(def);
  }

  // ============================================================================
  // Common Schemes
  // ============================================================================

  static readonly KeyboardMouse = new ControlScheme({
    name: 'Keyboard+Mouse',
    devices: ['keyboard', 'mouse'],
  });

  static readonly Gamepad = new ControlScheme({
    name: 'Gamepad',
    devices: ['gamepad'],
  });

  static readonly Touch = new ControlScheme({
    name: 'Touch',
    devices: ['touchscreen'],
  });

  static readonly KeyboardOnly = new ControlScheme({
    name: 'Keyboard',
    devices: ['keyboard'],
  });
}
